import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { capture } from "@/lib/analytics";
import { EVENTS } from "@/lib/analyticsEvents";
import {
  barLinks,
  FOOTER_GROUPS,
  MENU_EXTRA_LINKS,
  navTarget,
  navTestId,
  REGISTER_ACTION,
} from "@/lib/navigation";

// Les rendus de la navigation unique (RDEC-019), partagés par les deux
// en-têtes et les deux pieds de page. Chaque en-tête garde son habillage ; le
// contenu, lui, vient d'ici.

const trackRegister = (section) => () => capture(EVENTS.REGISTER_CTA_CLICKED, { section });

// Seuls deux liens de pied de page portent une intention mesurable. Les autres
// sont de la navigation ordinaire, déjà couverte par les pages vues.
//
// `register_cta_clicked` garde sa propriété `section`, partagée avec les
// boutons de l'accueil : la renommer ici scinderait l'événement en deux séries.
const trackFooterClick = (to) => {
  if (to === REGISTER_ACTION.to) return trackRegister("footer_nav");
  if (to === "/signataires")
    return () => capture(EVENTS.SIGNATORY_CLICKED, { source: "footer_nav" });
  return undefined;
};

const barLink =
  "link-underline whitespace-nowrap text-xs tracking-[0.18em] uppercase text-ink-body hover:text-gold-ink transition-colors duration-300";

/**
 * La barre : entrées du domaine, puis l'action « Enregistrer ma montre », en
 * lien marqué d'or. Masquée quand elle ne tient pas (voir useNavFit).
 */
export const HeaderNav = forwardRef(({ fits }, ref) => {
  const { t } = useLanguage();
  return (
    <nav
      ref={ref}
      data-testid="site-nav"
      className={`${fits ? "flex" : "hidden"} items-center gap-5 shrink-0`}
    >
      {barLinks().map((link) => (
        <Link key={link.to} to={link.to} data-testid={navTestId("nav", link)} className={barLink}>
          {t(link.label)}
        </Link>
      ))}
      <Link
        to={REGISTER_ACTION.to}
        onClick={trackRegister("header_nav")}
        data-testid="nav-register"
        className="link-underline whitespace-nowrap text-xs tracking-[0.18em] uppercase text-gold-ink hover:text-gold-ink transition-colors duration-300"
      >
        {t(REGISTER_ACTION.label)}
      </Link>
    </nav>
  );
});
HeaderNav.displayName = "HeaderNav";

/** Le menu replié : la barre entière, puis ce qu'elle laisse au pied de page. */
export const MenuNav = ({ onNavigate }) => {
  const { t } = useLanguage();
  const base =
    "flex items-center min-h-[48px] text-sm tracking-[0.18em] uppercase transition-colors duration-300 border-b border-ink/5";
  const item = `${base} text-ink-body hover:text-gold-ink`;
  return (
    <>
      {barLinks().map((link) => (
        <Link key={link.to} to={link.to} onClick={onNavigate} data-testid={navTestId("menu-nav", link)} className={item}>
          {t(link.label)}
        </Link>
      ))}
      <Link
        to={REGISTER_ACTION.to}
        onClick={() => {
          trackRegister("header_nav")();
          onNavigate?.();
        }}
        data-testid="menu-nav-register"
        className={`${base} text-gold-ink hover:text-gold-ink`}
      >
        {t(REGISTER_ACTION.label)}
      </Link>
      {MENU_EXTRA_LINKS.map((link) => (
        <Link key={link.to} to={link.to} onClick={onNavigate} data-testid={navTestId("menu-nav", link)} className={item}>
          {t(link.label)}
        </Link>
      ))}
    </>
  );
};

/** Le pied de page : toutes les destinations, en quatre groupes. */
export const FooterNav = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  return (
    <nav data-testid="site-footer-nav" className="grid grid-cols-2 md:grid-cols-4 gap-x-10 gap-y-8">
      {FOOTER_GROUPS.map((group) => (
        <div key={group.id}>
          <p className="text-[11px] tracking-[0.25em] uppercase text-gold-ink mb-4">{t(group.title)}</p>
          <ul className="flex flex-col gap-3">
            {group.links.map((link) => (
              <li key={link.to}>
                {link.external ? (
                  <a
                    href={link.to}
                    rel="noopener"
                    data-testid={navTestId("footer-link", link)}
                    className="link-underline text-sm text-ink-muted hover:text-gold-ink transition-colors duration-300"
                  >
                    {t(link.label)}
                  </a>
                ) : (
                  <Link
                    to={navTarget(link, user)}
                    onClick={trackFooterClick(link.to)}
                    data-testid={navTestId("footer-link", link)}
                    className="link-underline text-sm text-ink-muted hover:text-gold-ink transition-colors duration-300"
                  >
                    {t(link.label)}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
};
